import { useEffect, useState } from "react";
import { Share, Plus, Home, BellRing, X } from "lucide-react";

/**
 * IOSInstallPrompt — visible only when:
 *   1) the user is on iOS Safari, AND
 *   2) Born Day is NOT yet running as a Home-Screen-installed PWA.
 *
 * iOS 16.4+ requires Web Push to be requested from inside an installed
 * PWA, not from a Safari tab. This component is the human-friendly
 * walkthrough that closes that gap for non-technical family members.
 *
 * Props:
 *   compact  — boolean. When true, renders as a one-line dismissible
 *              banner (used on the Dashboard). Default renders as a
 *              full card (used on Profile).
 *   testIdPrefix — string. Lets us reuse the component on two pages
 *              without testid collisions.
 */
const STORAGE_KEY = "bd_ios_install_dismissed_v1";

function detectIOSNotStandalone() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
  if (!isIOS) return false;
  const standaloneByMedia =
    window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const standaloneByLegacy = navigator.standalone === true;
  return !(standaloneByMedia || standaloneByLegacy);
}

export default function IOSInstallPrompt({ compact = false, testIdPrefix = "ios-install" }) {
  const [shouldShow, setShouldShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setShouldShow(detectIOSNotStandalone());
    try {
      if (compact && window.localStorage?.getItem(STORAGE_KEY)) setDismissed(true);
    } catch (e) { /* ignore */ }
  }, [compact]);

  if (!shouldShow || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { window.localStorage?.setItem(STORAGE_KEY, "1"); } catch (e) { /* ignore */ }
  };

  if (compact) {
    return (
      <div
        className="flex items-center gap-3 px-3 py-2 rounded mb-3"
        style={{
          background: "color-mix(in srgb, var(--primary) 12%, transparent)",
          border: "1px solid color-mix(in srgb, var(--primary) 30%, var(--border))",
        }}
        data-testid={`${testIdPrefix}-banner`}
      >
        <BellRing size={16} style={{ color: "var(--primary)", flexShrink: 0 }} />
        <div className="flex-1 text-sm min-w-0">
          <strong>Want phone buzzes when family goes live?</strong>
          <span className="hidden sm:inline" style={{ color: "var(--text-dim)" }}>
            {" "}Tap <Share size={11} className="inline mb-0.5" /> Share → <em>Add to Home Screen</em>, then open Born Day from the new icon.
          </span>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="bd-btn-ghost"
          style={{ padding: "0.2rem", background: "transparent", border: "none", cursor: "pointer", color: "var(--text-dim)" }}
          aria-label="Dismiss"
          data-testid={`${testIdPrefix}-dismiss`}
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div
      className="bd-surface p-5"
      style={{
        border: "1px solid color-mix(in srgb, var(--primary) 35%, var(--border))",
        background: "color-mix(in srgb, var(--primary) 6%, var(--bg-soft))",
      }}
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="flex items-center gap-2 mb-1 font-bold text-lg">
        <BellRing size={18} style={{ color: "var(--primary)" }} />
        Add Born Day to your Home Screen
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--text-dim)" }}>
        On iPhone, Apple only lets us send phone buzzes when Born Day lives on your
        Home Screen — exactly like a regular app. Three taps:
      </p>
      <ol className="space-y-2" data-testid={`${testIdPrefix}-steps`}>
        <li className="flex items-start gap-3 p-3 rounded" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
          <span
            className="grid place-items-center font-bold bd-mono"
            style={{
              width: 26, height: 26, borderRadius: "50%",
              background: "var(--primary)", color: "var(--primary-fg)", fontSize: 13, flexShrink: 0,
            }}
          >1</span>
          <div className="flex-1 text-sm">
            Tap the <Share size={14} className="inline mb-0.5" style={{ color: "var(--primary)" }} /> <strong>Share</strong> icon
            at the bottom of Safari (looks like a box with an arrow pointing up).
          </div>
        </li>
        <li className="flex items-start gap-3 p-3 rounded" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
          <span
            className="grid place-items-center font-bold bd-mono"
            style={{
              width: 26, height: 26, borderRadius: "50%",
              background: "var(--primary)", color: "var(--primary-fg)", fontSize: 13, flexShrink: 0,
            }}
          >2</span>
          <div className="flex-1 text-sm">
            Scroll and tap <Plus size={14} className="inline mb-0.5" style={{ color: "var(--primary)" }} /> <strong>Add to Home Screen</strong>.
            Confirm the name (Born Day) and hit <strong>Add</strong>.
          </div>
        </li>
        <li className="flex items-start gap-3 p-3 rounded" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
          <span
            className="grid place-items-center font-bold bd-mono"
            style={{
              width: 26, height: 26, borderRadius: "50%",
              background: "var(--primary)", color: "var(--primary-fg)", fontSize: 13, flexShrink: 0,
            }}
          >3</span>
          <div className="flex-1 text-sm">
            Close Safari. Tap the new <Home size={14} className="inline mb-0.5" style={{ color: "var(--primary)" }} /> <strong>Born Day</strong> icon
            on your Home Screen, sign back in, and flip on <em>Phone alerts</em> below.
          </div>
        </li>
      </ol>
      <p className="text-xs mt-4" style={{ color: "var(--text-dim)" }}>
        Nothing downloads, no App Store. It&apos;s the same Born Day — just installed so iOS will let it buzz your phone.
      </p>
    </div>
  );
}

// Re-export for any caller that wants to detect this from a parent.
export { detectIOSNotStandalone };
